import type { FileReadFailureReason } from './hooks/useFileObjectUrl';

/**
 * The sentence a reader is shown when a file's bytes did not arrive (FR-067).
 *
 * One helper for all three content previews — `HtmlPreview`, `PdfPreview` and
 * `ImagePreview` — because each of them had its own copy of the wording, two of
 * them had only the "could not be loaded" copy for every reason at all, and a
 * deleted file read as a transport failure. With the mapping here they cannot
 * drift, and adding a reason is one edit rather than three.
 *
 * `noun` is what this surface calls the thing it was going to show — "page",
 * "PDF", "image". It is a noun, not a sentence fragment: the shapes below
 * supply the grammar.
 *
 * Only `'failed'` is worth offering a retry beside (see `RetryButton`): the
 * other three are answers, not unanswered questions.
 */
export function fileReadMessage(
  reason: FileReadFailureReason | undefined,
  noun: string,
): string {
  switch (reason) {
    case 'not-found':
      return `This ${noun} no longer exists.`;
    case 'not-permitted':
      return `You do not have access to this ${noun}.`;
    case 'session-expired':
      // Wording follows `SessionProvider`'s "Your session ended. Sign in again
      // to save this." — same two-sentence shape, named for a read.
      return `Your session has expired. Sign in again to see this ${noun}.`;
    default:
      return `This ${noun} could not be loaded.`;
  }
}
